import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import CategoryTabs from '@/components/base/CategoryTabs';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, articles } from '@/mocks/data';

const categoryMap: Record<string, string> = {};
categories.forEach((c) => {
  categoryMap[c.slug] = c.name;
});

const categoryImages: Record<string, string> = {
  'study-companion': 'https://readdy.ai/api/search-image?query=A%20warm%20and%20cozy%20home%20study%20environment%20with%20a%20wooden%20desk%20and%20books%2C%20soft%20natural%20light%20from%20a%20window%2C%20gentle%20and%20inviting%20atmosphere%2C%20cream%20and%20soft%20coral%20tones%2C%20minimalist%20interior%2C%20editorial%20photography&width=800&height=400&seq=cat-hero-study&orientation=landscape',
  'emotion-management': 'https://readdy.ai/api/search-image?query=A%20peaceful%20meditation%20corner%20with%20a%20soft%20cushion%20and%20warm%20candlelight%2C%20calm%20and%20serene%20atmosphere%2C%20cream%20and%20soft%20amber%20tones%2C%20minimalist%20interior%2C%20wellness%20lifestyle%20photography&width=800&height=400&seq=cat-hero-emotion&orientation=landscape',
  'parent-child-communication': 'https://readdy.ai/api/search-image?query=A%20mother%20and%20child%20sitting%20on%20a%20sofa%20having%20a%20warm%20conversation%2C%20soft%20afternoon%20sunlight%2C%20loving%20and%20gentle%20atmosphere%2C%20cream%20and%20soft%20sage%20green%20tones%2C%20minimalist%20living%20room%2C%20lifestyle%20photography&width=800&height=400&seq=cat-hero-comm&orientation=landscape',
  'education-planning': 'https://readdy.ai/api/search-image?query=An%20organized%20desk%20with%20a%20calendar%20and%20study%20materials%2C%20a%20roadmap%20concept%2C%20bright%20and%20hopeful%20atmosphere%2C%20cream%20and%20soft%20coral%20tones%2C%20minimalist%20interior%2C%20editorial%20photography&width=800&height=400&seq=cat-hero-plan&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const seoTitle = `${category.name}`;
  const seoDesc = `${category.description} 学生的妈妈为你提供${category.name}相关的实用文章与科学方法。`;
  const seoKeywords = `学生的妈妈,${category.name},${category.name}技巧,学生家长${category.name}`;

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={`https://lijinye.cn/category/${slug}`}
      />
      <SchemaOrg type="WebPage" data={{ name: seoTitle, description: seoDesc, url: `https://lijinye.cn/category/${slug}` }} />
      <SchemaOrg type="BreadcrumbList" data={{ itemListElement: [{ '@type': 'ListItem', position: 1, name: '首页', item: 'https://lijinye.cn' }, { '@type': 'ListItem', position: 2, name: category.name }] }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 pb-14">
          {/* Category Hero */}
          <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={categoryImages[category.slug] || categoryImages['study-companion']}
                alt={category.name}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
            <div className="relative z-10 h-full flex items-center">
              <div className="max-w-7xl mx-auto px-4 md:px-6 w-full">
                <div className="max-w-2xl">
                  <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category.name }]} />
                  <h1 className="mt-4 text-3xl md:text-4xl font-heading font-bold text-background-50">
                    {category.name}
                  </h1>
                  <p className="mt-3 text-background-200 leading-relaxed text-sm md:text-base">
                    {category.description}
                  </p>
                  <p className="mt-4 text-sm text-background-300">
                    共 {categoryArticles.length} 篇文章
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs + Article Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 mt-10">
            <div className="mb-10">
              <CategoryTabs activeSlug={slug} />
            </div>

            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类下暂无文章，敬请期待。</p>
                <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer text-sm">
                  返回首页浏览更多内容
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <ArticleCard
                    key={article.slug}
                    slug={article.slug}
                    title={article.title}
                    category={article.category}
                    excerpt={article.excerpt}
                    imageUrl={article.imageUrl}
                    author={article.author}
                    readTime={article.readTime}
                    date={article.date}
                    tags={article.tags}
                    categoryName={categoryMap[article.category]}
                    categorySlug={article.category}
                  />
                ))}
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}